import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import isObject from 'lodash/isObject'
import { SelectOption } from '@microservices/wiskey-react-components'

import { getDataFromState } from '@components/CommandParameters/helpers'

import {
  useFetchAllFormQuery,
  useFetchAllViewQuery,
  useFetchDictionariesByCategoryCodeQuery,
} from '@redux/api'
import { useFetchContextMenuListQuery } from '@redux/api/contextMenu.api'
import { useFetchAllObjectQuery } from '@redux/api/objects.api'
import { useFetchAllTooltipsQuery } from '@redux/api/tooltips.api'

import { usePrompt, useVariables } from '@hooks'
import { findColumnsByIds, getAvailableActionEvents } from '@helpers'
import { ACTION_CODE, CATEGORIES, EVENT_CODE, MODAL_TYPE, MODEL_TYPE } from '@constants'
import { AutocompleteOption, FormCommand } from '@types'

import { AddActionDialogType } from '../AddActionDialog'
import {
  convertRowToActionFormInitialValues,
  transformRedefinitionCommandsFormDataToRequestData,
} from '../helpers'
import { getDefaultValue } from '../helpers'

import { ActionForm, useInputs } from './useInputs'

export const useAddActionDialog = (props: AddActionDialogType) => {
  const {
    columns,
    objectCode,
    entityCode,
    onCloseModal,
    modalType,
    createAction,
    editAction,
    currentRow,
    onSetModalProperty,
    currentEntity,
    bindingValuesAction,
    actionTable,
    actionEventsCategoryCode,
    isDisableActionEventsFiltering,
    contextMenuEventsCategoryCode,
    variablesConfig,
    tooltipEventsCategoryCode,
    type,
  } = props

  const defaultValues: ActionForm = getDefaultValue()
  const methods = useForm<ActionForm>({ defaultValues })

  const [isLoading, setLoading] = useState(true)
  const [showActionCommandsModal, setShowActionCommandsModal] = useState<boolean>(false)
  const [currentNameScriptDialog, setCurrentNameScriptDialog] = useState('')
  const [isOpenScriptValueDialog, setIsOpenScriptValueDialog] = useState(false)

  const variables = useVariables({
    id: variablesConfig?.entityId,
    type: variablesConfig?.entityType,
  })

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
    setValue,
  } = methods

  const watchJson = watch('json')
  const watchActionCode = watch('actionCode')
  const watchJsValue = watch('jsValue')
  const watchVariable = watch('variable')
  const watchEvent = watch('eventCode')
  const watchFormObject = watch('formObject')
  const watchRedefinitionCommands = watch('redefinitionCommands')
  const watchDraggableElementText = watch('draggableElementText')
  const watchCommand = watch('command')

  const isOpenFormAction =
    watchActionCode === ACTION_CODE.OPEN_ANOTHER_FORM_MODEL ||
    watchActionCode === ACTION_CODE.OPEN_FORM_MODEL

  const isOpenViewAction = watchActionCode === ACTION_CODE.OPEN_VIEW

  const isContextMenu = watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU
  const isTooltip = watchActionCode == ACTION_CODE.OPEN_TOOLTIP

  let categoryCode: CATEGORIES = actionEventsCategoryCode || CATEGORIES.BINDING_VIEW_EVENTS_ACTION

  if (isContextMenu) {
    categoryCode = contextMenuEventsCategoryCode || CATEGORIES.BINDING_EVENTS_ACTION_CONTEXT_MENU
  }

  if (isTooltip) {
    categoryCode = tooltipEventsCategoryCode || CATEGORIES.BINDING_EVENTS_ACTION_TOOLTIP
  }

  usePrompt({ when: isDirty })

  const {
    data: bindingValuesActionEvents,
    isLoading: isLoadingBindingValuesEvents,
    isFetching: isFetchingBindingValuesEvents,
  } = useFetchDictionariesByCategoryCodeQuery(categoryCode)

  const loadingBindingValuesEvents = isLoadingBindingValuesEvents || isFetchingBindingValuesEvents

  const viewType = currentEntity?.type

  const {
    data: forms,
    isFetching: isFetchingAllForm,
    isLoading: isLoadingAllForm,
  } = useFetchAllFormQuery(
    {
      objectCode: (watchFormObject?.id as string) ?? '',
    },
    {
      skip: !watchFormObject,
    }
  )

  const {
    data: views,
    isFetching: isFetchingAllViews,
    isLoading: isLoadingAllViews,
  } = useFetchAllViewQuery(
    {
      objectCode: (watchFormObject?.id as string) ?? '',
    },
    {
      skip: !watchFormObject || !isOpenViewAction,
    }
  )

  const { data: contextMenus } = useFetchContextMenuListQuery({ objectCode }, { skip: !objectCode })
  const { data: tooltips } = useFetchAllTooltipsQuery({ objectCode }, { skip: !objectCode })

  const { data: objects, isLoading: isAllObjectsLoading } = useFetchAllObjectQuery(undefined, {
    skip: !isOpenFormAction && !isOpenViewAction,
  })

  const loadingAllForm = isFetchingAllForm || isLoadingAllForm
  const loadingAllViews = isFetchingAllViews || isLoadingAllViews

  const draggableElementTextOptions = useMemo(
    () => currentEntity?.columns?.map(({ code: id, title: label }) => ({ id, label })) || [],
    [currentEntity]
  )

  const contextMenusOptions = useMemo(
    () => contextMenus?.map(({ id, title: label }) => ({ id, label })) || [],
    [contextMenus]
  )

  const tooltipOptions = useMemo(
    () => tooltips?.map(({ id, title: label }) => ({ id, label })) || [],
    [tooltips]
  )

  const targetColumnsOptions = useMemo(
    () => columns.map(({ id, title: label }) => ({ id, label })) || [],
    [columns]
  )

  const selectedColumnOptions: AutocompleteOption[] = useMemo(
    () =>
      findColumnsByIds(currentRow?.columnIds, columns).map(({ id, title }) => ({
        id,
        label: title,
      })),
    [currentRow?.columnIds, columns]
  )

  const formCodeOptions: AutocompleteOption[] = useMemo(
    () =>
      forms?.data.map(form => ({
        id: form.code,
        label: form.title,
      })) ?? [],
    [forms]
  )

  const viewOptions: AutocompleteOption[] = useMemo(() => {
    return (
      views?.data?.map(view => ({
        id: view.id,
        label: view.title,
        code: view.code,
      })) || []
    )
  }, [views])

  const variablesOptions: AutocompleteOption[] = useMemo(
    () =>
      variables?.map(variable => ({
        id: variable.id,
        label: variable.title,
      })) ?? [],
    [variables]
  )

  const filteredActionEvents = isDisableActionEventsFiltering
    ? bindingValuesActionEvents
    : getAvailableActionEvents(bindingValuesActionEvents, actionTable, currentRow, watchActionCode)

  const isColumnUsedByAnotherAction = (columnId: string | number) =>
    actionTable?.rows.some(
      event =>
        String(event.id) !== String(currentRow?.id) &&
        (event.columnIds?.some(id => String(id) === String(columnId)) ||
          String(event.target?.id) === String(columnId))
    )

  const filteredTargetColumnsOptions = targetColumnsOptions.filter(
    targetColumn => !isColumnUsedByAnotherAction(targetColumn.id)
  )

  const actionTypeOptions: SelectOption[] = useMemo(
    () =>
      bindingValuesAction?.map(cat => ({
        id: cat.id,
        name: cat.code,
      })) || [],
    [bindingValuesAction]
  )

  const eventOptions: SelectOption[] = useMemo(
    () =>
      filteredActionEvents?.map(cat => ({
        id: cat.id,
        name: cat.code,
      })) || [],
    [filteredActionEvents]
  )

  const objectOptions = useMemo(() => {
    if (!objects) {
      return []
    }

    if (watchActionCode === ACTION_CODE.OPEN_FORM_MODEL) {
      const entityObject = objects.find(object => object.code === objectCode)

      if (!entityObject) {
        return []
      }

      return [{ id: entityObject.code, label: entityObject.title }]
    }

    return objects.map(object => {
      return {
        id: object.code,
        label: object.title,
      }
    })
  }, [objects, watchActionCode])

  useEffect(() => {
    if (currentRow) {
      reset(convertRowToActionFormInitialValues({ currentRow, selectedColumnOptions }))

      setLoading(false)
    }
  }, [currentRow, selectedColumnOptions, reset])

  useEffect(() => {
    onSetModalProperty?.(MODEL_TYPE.VIEW_ACTION)
  }, [])

  const handleSave = handleSubmit((data: ActionForm) => {
    const {
      id,
      code,
      title,
      actionCode = ACTION_CODE.OPEN_FORM_MODEL,
      eventCode = EVENT_CODE.ON_ROW_CLICK,
      params = '',
      formObject,
      objectFormCode,
      json,
      contextMenu,
      tooltip,
      targetColumns,
      command,
      redefinitionCommands,
      view,
    } = data

    const columnIds = targetColumns?.map(column => column.id)

    const actionPayload = {
      code,
      title,
      eventCode,
      actionCode,
      id,
      columnIds,
      viewCode: view?.code || entityCode,
      viewType,
      contextMenuId: contextMenu?.id,
      tooltipId: tooltip?.id,
      variableId: watchVariable?.id || null,
      jsValue: watchJsValue || '',
      objectViewId: view?.id,
      objectViewTitle: view?.label,
      draggableElementFieldCode: watchDraggableElementText?.id || '',
      draggableElementFieldTitle: watchDraggableElementText?.label || '',
      redefinitionCommands:
        transformRedefinitionCommandsFormDataToRequestData(redefinitionCommands),
      command: command?.length
        ? {
            ...command[0],
            name: command[0].name?.id,
            objectCode: command[0].sourceCommand?.id,
            objectTitle: command[0].sourceCommand?.label,
            resource: command[0].sourceType?.id,
            type: command[0].type?.id,
            parameters: getDataFromState(command[0].parameters),
            body: getDataFromState(command[0].body),
          }
        : null,
      ...(json
        ? { params }
        : {
            objectCode:
              isObject(formObject) && formObject?.id ? formObject.id.toString() : undefined,
            objectTitle:
              isObject(formObject) && formObject?.label ? formObject.label.toString() : undefined,
            objectFormCode:
              isObject(objectFormCode) && objectFormCode?.id
                ? objectFormCode.id.toString()
                : undefined,
            objectFormTitle:
              isObject(objectFormCode) && objectFormCode?.id ? objectFormCode.label : undefined,
          }),
    }

    if (modalType === MODAL_TYPE.EDIT && currentRow) {
      const action = editAction?.(actionPayload)

      if (typeof action?.then === 'function') {
        action
          .then(() => {
            onCloseModal()
          })
          .catch(console.error)

        return
      }

      onCloseModal()

      return
    }

    const action = createAction?.(actionPayload)

    if (typeof action?.then === 'function') {
      action
        .then(() => {
          onCloseModal()
          reset(defaultValues)
        })
        .catch(console.error)

      return
    }

    onCloseModal()
    reset(defaultValues)
  })

  const { actionInputs, actionValueInputs, actionJSONValueInputs, actionViewInputs } = useInputs({
    setValue,
    actionTypeOptions,
    eventOptions,
    objectOptions,
    watchJson,
    watchEvent,
    watchActionCode,
    watchFormObject,
    formCodeOptions,
    loadingAllForm,
    loadingAllViews,
    targetColumnsOptions: filteredTargetColumnsOptions,
    contextMenusOptions,
    tooltipOptions,
    loadingBindingValuesEvents,
    isAllObjectsLoading,
    isOpenFormAction,
    modalType,
    variablesOptions,
    draggableElementTextOptions,
    setIsOpenScriptValueDialog,
    setCurrentNameScriptDialog,
    viewOptions,
    type,
  })

  const handleShowActionCommandsModal = () => setShowActionCommandsModal(true)
  const handleCloseActionCommandsModal = () => setShowActionCommandsModal(false)

  const handleChangeRedefinitionCommands = (value?: FormCommand[] | null) => {
    setValue('redefinitionCommands', value, {
      shouldDirty: true,
    })
  }

  return {
    data: {
      actionInputs,
      actionValueInputs,
      actionJSONValueInputs,
      actionViewInputs,
      isOpenFormAction,
      isOpenViewAction,
    },
    state: {
      isLoading,
      currentNameScriptDialog,
      isOpenScriptValueDialog,
      methods,
      isDirty,
      watchRedefinitionCommands,
      watchJson,
      watchJsValue,
      watchEvent,
      watchCommand,
      watchActionCode,
      showActionCommandsModal,
    },
    handlers: {
      handleShowActionCommandsModal,
      handleCloseActionCommandsModal,
      handleChangeRedefinitionCommands,
      handleSave,
      setIsOpenScriptValueDialog,
      setCurrentNameScriptDialog,
    },
  }
}
